/**
 * ВТОРИЯТ СВИДЕТЕЛ: какво вижда КАСАТА за облака.
 *
 * Терминалът знае само какво се случва с неговия Wi-Fi. Касата е друга
 * машина, на друга мрежова карта, често на кабел — и сондира облака сама.
 * Двамата заедно отговарят на въпроса, който терминалът сам не може да
 * зададе: „интернетът на ОБЕКТА ли падна, или само моят".
 *
 * Отговорът е решаващ, защото `POST {edge}/mode/enter` замразява ЦЕЛИЯ обект:
 * един терминал със счупен Wi-Fi не бива да спре ресторанта, докато касата в
 * същия момент разговаря с облака. Затова:
 *
 *  • касата казва `up`  → проблемът е в ТОВА устройство, никакво замразяване;
 *  • касата казва `down` → обектът е без интернет, влиза се бързо (4 сонди);
 *  • касата казва `app_down` → мрежата е жива, приложението е долу. Това
 *    почти винаги е наш деплой, затова се чака 60 s;
 *  • касата МЪЛЧИ или казва `unknown` (по-стар билд, още не е сондирала) →
 *    свидетел няма и важи предпазливият праг (10 сонди). Мълчаща каса НЕ е
 *    грешка и НЕ е „облакът е жив".
 *
 * Самото питане зарежда наблюдателния прозорец на касата (30 s), в който тя
 * сондира облака често — договорът е в `Docs/edge-spec.md` (FiscalAPI).
 * Модулът само ПИТА и превежда; решенията са в `edgeMode.ts`.
 */
import {
  getEdgeSyncState,
  type CloudVerdict,
  type EdgeSyncStateOut,
} from "../../api/edge";
import { devLog, getActiveEdgeTicket } from "./edgeRuntime";
import { getEdgeBaseUrl, type EdgeMode } from "./edgeState";

/**
 * По-стара от това присъда не е свидетелство. Касата сондира на 2 s в
 * наблюдателния прозорец; 10 s значи „пет пропуснати сонди" — тогава тя вече
 * не знае какво става, а стар спомен „облакът беше жив" би държал терминала
 * извън локален режим точно по време на аварията.
 */
export const WITNESS_MAX_AGE_MS = 10_000;

/** Колко дълго един и същ отговор се преизползва. Под интервала на бързата
 * фаза (2 s), за да има прясна присъда на всяка сонда, но паралелните
 * повиквания в рамките на един тик да са ЕДНА заявка. */
const WITNESS_CACHE_MS = 1_500;

/**
 * Присъдата на касата, сведена до това, което машината на режима ползва.
 *
 * `silent` е ОТДЕЛЕН случай от `seen: "unknown"`: първото значи „касата не
 * отговори изобщо" (мъртва, изключена, забранен достъп до локалната мрежа),
 * второто — „касата отговори, но не знае". За решението и двете значат „няма
 * свидетел", но за човека и за лога значат различни неща.
 */
export interface EdgeCloudWitness {
  silent: boolean;
  seen: CloudVerdict;
  ageMs: number | null;
  mqttConnected: boolean | null;
  /** В кой режим е САМАТА каса в момента (замразена ли е вече). */
  tillMode: EdgeMode | null;
}

/** Какво важи за матрицата на праговете: свежа присъда, иначе „няма свидетел". */
export type WitnessVerdict = "up" | "app_down" | "down" | "none";

export const SILENT_WITNESS: EdgeCloudWitness = {
  silent: true,
  seen: "unknown",
  ageMs: null,
  mqttConnected: null,
  tillMode: null,
};

/**
 * Присъдата, годна за решение. Остаряла присъда СЕ ИЗХВЪРЛЯ: по-добре
 * предпазливият праг, отколкото решение по спомен.
 */
export function witnessVerdict(witness: EdgeCloudWitness): WitnessVerdict {
  if (witness.silent || witness.seen === "unknown") return "none";
  if (witness.ageMs !== null && witness.ageMs > WITNESS_MAX_AGE_MS) return "none";
  return witness.seen;
}

/** Касата вече е замразена — обектът работи срещу нея, каквото и да мисли
 * облакът. Тогава ветото ѝ („облакът е жив") няма значение: няма какво да
 * се замразява, замразено е. */
export const witnessSaysTillIsLocal = (witness: EdgeCloudWitness): boolean =>
  witness.tillMode === "local";

function readVerdict(value: unknown): CloudVerdict {
  return value === "up" || value === "app_down" || value === "down"
    ? value
    : "unknown";
}

function readAge(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

/**
 * Отговор от КАСАТА ли е това изобщо. Каса, върната на билд без локален режим,
 * отговаря на непознат път с 200 и HTML-а на своя панел (измерено срещу каса
 * 1.77.0) — същата проверка като в `edgeSyncState.ts`.
 */
function isEdgeAnswer(body: unknown): body is EdgeSyncStateOut {
  if (typeof body !== "object" || body === null) return false;
  const { synced, mode } = body as { synced?: unknown; mode?: unknown };
  return typeof synced === "boolean" && (mode === "cloud" || mode === "local");
}

let cached: { at: number; witness: EdgeCloudWitness } | null = null;
let inFlight: Promise<EdgeCloudWitness> | null = null;

/** Забравя кешираното — при смяна на режима и в тестовете. */
export function forgetCloudWitness() {
  cached = null;
}

/**
 * Пита касата какво вижда. НИКОГА не хвърля: мълчанието ѝ е отговор
 * (`SILENT_WITNESS`), а не изключение, което би трябвало да се лови на
 * всяко място, откъдето машината на режима пита.
 */
export function askCloudWitness(): Promise<EdgeCloudWitness> {
  const now = Date.now();
  if (cached && now - cached.at < WITNESS_CACHE_MS) {
    return Promise.resolve(cached.witness);
  }
  if (inFlight) return inFlight;
  inFlight = runAsk().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function runAsk(): Promise<EdgeCloudWitness> {
  const base = getEdgeBaseUrl();
  if (!base) return SILENT_WITNESS;
  let witness = SILENT_WITNESS;
  try {
    const state = await getEdgeSyncState(base, getActiveEdgeTicket());
    if (isEdgeAnswer(state)) {
      witness = {
        silent: false,
        seen: readVerdict(state.cloud_seen),
        ageMs: readAge(state.cloud_seen_age_ms),
        mqttConnected:
          typeof state.mqtt_connected === "boolean"
            ? state.mqtt_connected
            : null,
        tillMode: state.mode === "local" ? "local" : "cloud",
      };
    } else {
      devLog("the till answered the witness with something that is not a status");
    }
  } catch (error) {
    devLog("cloud witness failed", error);
  }
  cached = { at: Date.now(), witness };
  return witness;
}
